"use client"

import styled from "@emotion/styled"
import { Box, Pagination } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Container } from "ui"
import { sharedColors as colors } from "ui/theme/colors"
import { extractProperty, JobsObject } from "utils"
import { fetchOnlyJobs } from "utils/api"
import WhiteBgHeading from "../../Projects/Marketing/ApartmentMarketingForRent/WhiteBgHeading"
import { CareerLobbyMessages } from "../Careers"
import FiltersComponent from "./FiltersComponent"
import JobsDisplay from "./JobsDisplay"
import { NoJobsFound } from "./NoJobsFound"

export type SearchParams = { [key: string]: string | string[] | undefined }

const JobsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
  padding-top: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
  }
`
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 60px;

  .MuiPagination-ul {
    gap: 16px;
  }

  .MuiPaginationItem-page {
    font-family: var(--rag);
    color: ${colors.newBlack};
    border-radius: 50%;
    font-size: 16px;
    font-weight: 400;
    min-width: 24px;
    height: 24px;
    border: 1px solid ${colors.lightGrey};
    margin: 0;
  }

  .MuiPaginationItem-page.Mui-selected {
    background-color: ${colors.darkBlue4rentMarketing};
    color: ${colors.white};
  }
`
export type FilterState = {
  areaIds: number[]
  companyIds: number[]
  jobScopeIds: number[]
  professionIds: number[]
  orderIds: number[]
  companyDivisions: number[]
  searchText: string
}

type ArrayFilterKey = Exclude<keyof FilterState, "searchText">

const arrayFilterKeys: ArrayFilterKey[] = [
  "areaIds",
  "companyIds",
  "jobScopeIds",
  "professionIds",
  "orderIds",
  "companyDivisions",
]

const toNumberArray = (values: (string | number)[], allowedValues?: Set<number>) => {
  const unique = new Set<number>()
  values.forEach((value) => {
    const numeric = typeof value === "number" ? value : Number(value)
    if (Number.isNaN(numeric)) return
    if (allowedValues && !allowedValues.has(numeric)) return
    unique.add(numeric)
  })
  return Array.from(unique).sort((a, b) => a - b)
}

const normalizeState = (
  nextState: FilterState,
  allowedValues: Record<ArrayFilterKey, Set<number> | undefined>,
): FilterState => {
  const normalized = arrayFilterKeys.reduce((acc, key) => {
    acc[key] = toNumberArray(nextState[key] ?? [], allowedValues[key])
    return acc
  }, {} as FilterState)

  return {
    ...nextState,
    ...normalized,
    searchText: nextState.searchText ?? "",
  }
}

const arraysEqual = (a: number[], b: number[]) =>
  a.length === b.length && a.every((value, index) => value === b[index])

const statesEqual = (a: FilterState, b: FilterState) =>
  arrayFilterKeys.every((key) => arraysEqual(a[key], b[key])) &&
  (a.searchText ?? "") === (b.searchText ?? "")

const serializeFiltersToQuery = (state: FilterState, page: number) => {
  const params = new URLSearchParams()

  arrayFilterKeys.forEach((key) => {
    if (state[key] && state[key].length > 0) {
      params.set(key, state[key].join(","))
    }
  })

  if (state.searchText && state.searchText.trim()) {
    params.set("searchText", state.searchText.trim())
  }

  if (page > 1) {
    params.set("page", String(page))
  }

  return params.toString()
}

const parseFiltersFromSearchParams = (
  params: SearchParams,
  allowedValues: Record<ArrayFilterKey, Set<number> | undefined>,
): { parsedState: FilterState; parsedPage: number } => {
  const parsedState = arrayFilterKeys.reduce((acc, key) => {
    const rawValue = params[key]
    const allValues = Array.isArray(rawValue)
      ? rawValue.flatMap((v) => v.split(","))
      : rawValue
      ? rawValue.split(",")
      : []
    acc[key] = toNumberArray(allValues, allowedValues[key])
    return acc
  }, {} as FilterState)

  const rawSearchText = params.searchText
  const searchText = Array.isArray(rawSearchText) ? rawSearchText.join(" ") : rawSearchText ?? ""

  parsedState.searchText = searchText.trim()

  const rawPage = Number(params.page)
  const parsedPage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1

  return { parsedState, parsedPage }
}

const filterParams = (params: FilterState) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        acc[key] = value
      }
      return acc
    }

    if (typeof value === "string") {
      const trimmed = value.trim()
      if (trimmed) {
        acc[key] = trimmed
      }
      return acc
    }

    if (value !== 0 && value !== undefined && value !== null) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, unknown>)
}

type Props = {
  jobs: JobsObject
  filters: any
  locale: string
  messages: CareerLobbyMessages
  allData: any
  companyDivisionId: number[]
  isGroupWebsite?: boolean
  jobSearchData?: any
  noJobsFoundData?: any
}
function Jobs({
  filters,
  locale,
  messages,
  allData,
  companyDivisionId,
  isGroupWebsite,
  jobSearchData,
  noJobsFoundData,
}: Props) {
  const urlSearchParams = useSearchParams()
  // Convert URLSearchParams to plain object for parseFiltersFromSearchParams
  const searchParams: SearchParams = useMemo(() => {
    const obj: SearchParams = {}
    urlSearchParams.forEach((value, key) => {
      const existing = obj[key]
      if (existing) {
        obj[key] = Array.isArray(existing) ? [...existing, value] : [existing, value]
      } else {
        obj[key] = value
      }
    })
    return obj
  }, [urlSearchParams])
  const router = useRouter()
  const pathname = usePathname()
  const isInitialMount = useRef(true)
  const listTopRef = useRef<HTMLDivElement | null>(null)
  const hasPagedOnceRef = useRef(false)

  const allowedFilterValues = useMemo(
    () => ({
      areaIds: new Set<number>(filters?.avialableAreas?.map((item: any) => item.id) ?? []),
      companyIds: new Set<number>(filters?.avialableCompanies?.map((item: any) => item.id) ?? []),
      jobScopeIds: new Set<number>(filters?.avialableJobScopes?.map((item: any) => item.id) ?? []),
      professionIds: new Set<number>(
        filters?.avialableProfessions?.map((item: any) => item.id) ?? [],
      ),
      orderIds: undefined,
      companyDivisions: undefined,
    }),
    [filters],
  )

  const { parsedState, parsedPage } = useMemo(
    () => parseFiltersFromSearchParams(searchParams, allowedFilterValues),
    [allowedFilterValues, searchParams],
  )

  // Check if URL has any filters that require fetching
  const hasUrlFilters = useMemo(() => {
    const hasArrayFilters = arrayFilterKeys.some(
      (key) => parsedState[key] && parsedState[key].length > 0,
    )
    const hasSearchText = Boolean(parsedState.searchText?.trim())
    return hasArrayFilters || hasSearchText
  }, [parsedState])

  const [state, setState] = useState<FilterState>(parsedState)
  const [page, setPage] = useState(parsedPage)

  const handleFilterChange = useCallback(
    (nextState: SetStateAction<FilterState>) => {
      setState((prev) => {
        const resolvedState = typeof nextState === "function" ? nextState(prev) : nextState
        return normalizeState(resolvedState, allowedFilterValues)
      })
      setPage(1)
    },
    [allowedFilterValues],
  )

  const initialJobsData = (allData as any)?.jobs ?? allData
  const pageSize = initialJobsData?.pageSize ?? 9
  const initialItems = initialJobsData?.items ?? []

  const {
    data: jobsData = initialJobsData,
    isFetching,
    isLoading,
  } = useQuery<JobsObject>({
    queryKey: ["jobs", state, page, pageSize],
    queryFn: async () => {
      const params = {
        ...filterParams({ ...state, companyDivisions: companyDivisionId }),
        requestPage: Math.max(0, page - 1),
        pageSize,
      }

      return await fetchOnlyJobs(params)
    },
    // Only use initialData when no URL filters - otherwise fetch on mount
    initialData: hasUrlFilters ? undefined : initialJobsData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  useEffect(() => {
    if (jobsData?.totalPageCount && page > jobsData.totalPageCount) {
      setPage(1)
    }
  }, [jobsData?.totalPageCount, page])

  useEffect(() => {
    setState((prev) => (statesEqual(prev, parsedState) ? prev : parsedState))
    setPage((prev) => (prev === parsedPage ? prev : parsedPage))
  }, [parsedPage, parsedState])

  useEffect(() => {
    // Skip URL update on initial mount to avoid unnecessary navigation
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const nextQuery = serializeFiltersToQuery(state, page)
    const currentQuery = new URLSearchParams(window.location.search).toString()
    if (nextQuery === currentQuery) return

    const url = nextQuery ? `${pathname}?${nextQuery}` : pathname
    router.push(url, { scroll: false })
  }, [page, pathname, router, state])

  const results = jobsData?.items ?? []

  const totalCount = jobsData?.totalCount ?? 0
  const totalPages = jobsData?.totalPageCount
    ? Number(jobsData.totalPageCount)
    : Math.ceil(totalCount / (jobsData?.pageSize ?? (pageSize || 1)))
  const currentPage = jobsData?.pageIndex !== undefined ? jobsData.pageIndex + 1 : page

  useEffect(() => {
    if (!hasPagedOnceRef.current) {
      hasPagedOnceRef.current = true
      return
    }
    // Keep focus anchored on the jobs list when pagination changes
    const topEl = listTopRef.current
    if (topEl) {
      const topOffset = topEl.getBoundingClientRect().top + window.scrollY - 24
      window.scrollTo({ top: topOffset, behavior: "smooth" })
    }
  }, [currentPage])

  const darkTitle = extractProperty(jobSearchData, "darkTitle")
  const lightTitle = extractProperty(jobSearchData, "lightTitle")
  const subTitle = extractProperty(jobSearchData, "subTitle")

  const showNoResults = !isLoading && !isFetching && results.length === 0

  return (
    <Container>
      <Box sx={{ height: "60px" }} />
      <WhiteBgHeading
        isCentered
        title={`<span>${darkTitle}</span> <strong>${lightTitle}</strong>`}
        subTitle={subTitle}
        subTitleFontWeight={300}
        desktopMarginBottom={8}
      />
      <div ref={listTopRef} />
      <JobsContainer>
        {initialItems.length > 0 && (
          <FiltersComponent
            filters={filters}
            setState={handleFilterChange}
            state={state}
            messages={messages}
            companyDivisionId={companyDivisionId}
          />
        )}
        {showNoResults ? (
          <NoJobsFound data={noJobsFoundData} />
        ) : (
          <JobsDisplay
            totalCount={totalCount}
            jobs={[...results]}
            isFetching={isFetching}
            isLoading={isLoading}
            locale={locale}
            messages={messages}
            filters={filters}
            isGroupWebsite={isGroupWebsite}
            pageSize={pageSize}
          />
        )}
      </JobsContainer>
      {!showNoResults && totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            count={totalPages}
            page={currentPage}
            color="primary"
            shape="rounded"
            size="small"
            onChange={(_event, value) => setPage(value)}
            siblingCount={1}
            boundaryCount={1}
            sx={{}}
          />
        </PaginationContainer>
      )}
    </Container>
  )
}

export default Jobs
